<div class="relative">
  <button
    class="flex h-10 items-center gap-3 rounded-full border px-5 py-2 transition-colors duration-300"
    [class]="
      isOpen() || selectedCount()
        ? 'border-neutral-900 bg-neutral-900 text-white'
        : 'border-neutral-800 bg-white text-neutral-800 hover:bg-neutral-50'
    "
    [attr.aria-expanded]="isOpen()"
    [attr.aria-controls]="title() + 'Dialog'"
    (click)="toggleDialog.emit()"
    (keydown.enter)="toggleDialog.emit()"
    (keydown.space)="toggleDialog.emit(); $event.preventDefault()"
    type="button"
  >
    <span class="text-base leading-normal font-normal">
      {{ title() }}
    </span>
    @if (!isOpen()) {
      <svg
        class="h-4 w-4"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8 3V13M3 8H13"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    }
    @if (isOpen()) {
      <svg
        class="h-4 w-4"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 8H13"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    }
  </button>

  @if (isOpen()) {
    <div
      class="shadow-lg filter-dialog-container absolute top-full left-0 z-[100] mt-2 h-[390px] max-h-[390px] w-[320px] max-w-[320px] overflow-hidden rounded-lg border border-neutral-800 bg-white"
      [id]="title() + 'Dialog'"
      [attr.aria-label]="title() + ' selection dialog'"
      (click)="$event.stopPropagation()"
      (keydown.escape)="toggleDialog.emit()"
      role="dialog"
    >
      <div class="flex h-full w-full flex-col overflow-hidden">
        <!-- Header with selected count and clear button -->
        <div
          class="flex h-10 flex-shrink-0 items-center justify-between px-5 pt-5 pb-0"
        >
          <div class="flex items-center">
            <div
              class="truncate text-base leading-normal font-normal text-primary-text"
            >
              {{ selectedCount() ?? 0 }}
              {{ 'components.filter.selected' | translate }}
            </div>
          </div>
          <button
            class="text-base leading-normal font-normal"
            [class.text-orange-500]="(selectedCount() ?? 0) > 0"
            [class.font-medium]="(selectedCount() ?? 0) > 0"
            [class.text-neutral-300]="(selectedCount() ?? 0) === 0"
            [disabled]="(selectedCount() ?? 0) === 0"
            (click)="clearSelection.emit()"
          >
            {{ 'components.filter.clearSelection' | translate }}
          </button>
        </div>
        <!-- Search input (optional) -->
        @if (showSearch()) {
          <div class="px-5 pt-2">
            <div class="relative">
              <input
                class="h-10 w-full rounded-md border border-neutral-300 bg-white pr-10 pl-10 text-sm focus:border-neutral-800 focus:outline-none"
                #searchInput
                [placeholder]="placeholder()"
                [ngModel]="searchTerm() ?? ''"
                (ngModelChange)="searchTermChange.emit($event)"
                type="text"
              />
              <div class="absolute top-1/2 left-3 -translate-y-1/2 transform">
                <svg
                  class="h-4 w-4 text-neutral-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <!-- Clear button -->
              @if (searchTerm()) {
                <button
                  class="group absolute top-1/2 right-3 -translate-y-1/2 transform rounded-full p-1 transition-colors duration-200 hover:bg-neutral-100"
                  (click)="clearSearch()"
                  (keydown.enter)="clearSearch()"
                  (keydown.space)="clearSearch(); $event.preventDefault()"
                  aria-label="Clear search"
                >
                  <svg
                    class="h-4 w-4 text-neutral-500 transition-colors duration-200 group-hover:text-neutral-700"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              }
            </div>
          </div>
        }
        <!-- Items container with scroll functionality -->
        <div
          class="w-full overflow-x-hidden overflow-y-auto px-0 py-2.5"
          #scrollContainer
          [ngClass]="{
            'h-[350px] max-h-[350px]': !showSearch(),
            'h-[302px] max-h-[302px]': showSearch(),
          }"
          (scroll)="onScroll($event)"
        >
          <!-- Loading indicator -->
          @if (isLoading()) {
            <div class="flex items-center justify-center p-4">
              <div
                class="h-10 w-10 animate-spin rounded-full border-4 border-orange-500/30 border-t-orange-500"
              ></div>
              <p class="text-neutral-600">
                {{ 'common.loading.default' | translate }}
              </p>
            </div>
          }
          <!-- No Results -->
          @if (!(isLoading() || (items ?? []).length)) {
            <div class="flex items-center justify-center p-4 text-center">
              <span class="text-sm text-neutral-600">
                {{ 'pages.home.noSearchResults' | translate }}
              </span>
            </div>
          }
          <!-- Item list with proper styling and event handling -->
          @if (!isLoading() && items && items.length > 0) {
            @for (item of items; track trackById($index, item)) {
              <div>
                <!-- Custom template if provided -->
                @if (customItemTemplate) {
                  <ng-container
                    *ngTemplateOutlet="
                      customItemTemplate;
                      context: { item: item }
                    "
                  ></ng-container>
                }
                <!-- Default item template -->
                @if (!customItemTemplate) {
                  <dhp-filter-item
                    [name]="
                      item.additionalProps
                        ? (item.additionalProps | formatAuthorName)
                        : item.name
                    "
                    [selected]="item.selected"
                    (itemSelected)="onItemSelected(item)"
                  ></dhp-filter-item>
                }
              </div>
            }
          }
        </div>
      </div>
    </div>
  }
</div>
